import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Copy, Loader2, ExternalLink, Shield } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const USDTPaymentDialog = ({ open, onOpenChange, artworkId, amountUsd, artworkTitle, onSuccess }) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState('idle');
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    if (open && artworkId && amountUsd > 0) {
      createOrder();
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [open]);

  const createOrder = async () => {
    setStatus('creating');
    try {
      const res = await axios.post(`${API}/usdt/create-order`, {
        artwork_id: artworkId,
        amount_usd: amountUsd,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setOrder(res.data);
      setStatus('waiting');
      startPolling(res.data.order_id);
    } catch (err) {
      setStatus('error');
      toast.error(err.response?.data?.detail || 'Failed to create USDT order');
    }
  };

  const startPolling = (orderId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/usdt/check-payment/${orderId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.status === 'paid') {
          clearInterval(pollRef.current);
          setStatus('paid');
          toast.success(t('checkout.paymentSuccess', 'Payment confirmed!'));
          if (onSuccess) onSuccess(res.data);
        }
      } catch {
        // Continue polling
      }
    }, 8000);
  };

  const copyAddress = () => {
    if (!order?.wallet_address) return;
    navigator.clipboard.writeText(order.wallet_address);
    setCopied(true);
    toast.success(t('common.copied', 'Copied!'));
    setTimeout(() => setCopied(false), 2000);
  };

  const copyAmount = () => {
    if (!order?.amount_usdt) return;
    navigator.clipboard.writeText(String(order.amount_usdt));
    toast.success(t('common.copied', 'Copied!'));
  };

  const handleClose = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setOrder(null);
    setStatus('idle');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="usdt-payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-lg font-bold text-teal-600">USDT</span>
            <span>{t('checkout.payment', 'Payment')}</span>
            <Badge className="bg-green-100 text-green-700 text-xs">TRC-20</Badge>
          </DialogTitle>
        </DialogHeader>

        {status === 'creating' && (
          <div className="flex flex-col items-center py-10">
            <Loader2 className="w-8 h-8 animate-spin text-teal-500 mb-3" />
            <p className="text-sm text-gray-500">{t('checkout.creatingOrder', 'Creating order...')}</p>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center py-8">
            <p className="text-red-500 mb-4">{t('checkout.orderFailed', 'Failed to create order')}</p>
            <Button onClick={createOrder}>{t('common.retry', 'Retry')}</Button>
          </div>
        )}

        {status === 'waiting' && order && (
          <div className="space-y-4">
            {/* Artwork info */}
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-sm text-gray-500">{artworkTitle}</p>
              <p className="text-2xl font-bold text-teal-600 mt-1">{order.amount_usdt} USDT</p>
              <p className="text-xs text-gray-400">≈ ${order.amount_usd || order.amount_usdt} USD ({order.amount_kwh || amountUsd} kWh)</p>
            </div>

            {/* QR Code */}
            <div className="flex justify-center">
              <div className="bg-white p-3 rounded-xl border-2 border-teal-100">
                <img
                  src={`data:image/png;base64,${order.qr_image_base64}`}
                  alt="USDT Wallet QR"
                  className="w-48 h-48"
                />
              </div>
            </div>

            {/* Wallet Address */}
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('checkout.walletAddress', 'Wallet Address')} (TRON)</p>
              <div
                className="flex items-center gap-2 bg-gray-50 rounded-lg p-3 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={copyAddress}
                data-testid="copy-wallet-address"
              >
                <code className="text-xs font-mono flex-1 break-all text-gray-700">{order.wallet_address}</code>
                {copied ? (
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                ) : (
                  <Copy className="w-4 h-4 text-gray-400 flex-shrink-0" />
                )}
              </div>
            </div>

            {/* Amount to send */}
            <div
              className="flex items-center justify-between bg-teal-50 rounded-lg p-3 cursor-pointer hover:bg-teal-100 transition-colors"
              onClick={copyAmount}
              data-testid="copy-amount"
            >
              <div>
                <p className="text-xs text-teal-600">{t('checkout.exactAmount', 'Send exactly')}</p>
                <p className="text-lg font-bold text-teal-700">{order.amount_usdt} USDT</p>
              </div>
              <Copy className="w-4 h-4 text-teal-400" />
            </div>

            {/* Status indicator */}
            <div className="flex items-center justify-center gap-2 py-2">
              <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
              <span className="text-sm text-amber-600">{t('checkout.waitingPayment', 'Waiting for payment...')}</span>
            </div>

            {/* Important notes */}
            <div className="bg-amber-50 rounded-lg p-3 space-y-1">
              <div className="flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5 text-amber-600" />
                <p className="text-xs font-medium text-amber-700">{t('checkout.important', 'Important')}</p>
              </div>
              <ul className="text-xs text-amber-600 space-y-0.5 ml-5 list-disc">
                <li>{t('checkout.usdtNote1', 'Use TRON (TRC-20) network only')}</li>
                <li>{t('checkout.usdtNote2', 'Send the exact amount shown above')}</li>
                <li>{t('checkout.usdtNote3', 'Payment auto-detected in 1-3 minutes')}</li>
              </ul>
            </div>

            {/* Tronscan link */}
            <a
              href={`https://tronscan.org/#/address/${order.wallet_address}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1 text-xs text-teal-500 hover:underline"
            >
              {t('checkout.viewOnBlockchain', 'View on TronScan')}
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}

        {status === 'paid' && (
          <div className="text-center py-8 space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <div>
              <p className="text-lg font-bold text-green-700">{t('checkout.paymentConfirmed', 'Payment Confirmed!')}</p>
              <p className="text-sm text-gray-500 mt-1">{artworkTitle}</p>
              <p className="text-xl font-bold text-teal-600 mt-2">{order?.amount_usdt} USDT</p>
            </div>
            {order?.tx_hash && (
              <a
                href={`https://tronscan.org/#/transaction/${order.tx_hash}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-teal-500 hover:underline inline-flex items-center gap-1"
              >
                TX: {order.tx_hash.slice(0, 16)}...
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
            <Button onClick={handleClose} className="bg-green-600 hover:bg-green-700 w-full">
              {t('common.done', 'Done')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default USDTPaymentDialog;
